import { maskFromRows, type Mask } from './mask';

/**
 * Canonical authored masks.
 *
 * Shipping silhouettes as text rather than PNG removes the entire cross-browser image
 * decoding question from the deterministic path: there is no canvas, no colour management
 * and no device-pixel-ratio anywhere in level generation. `scripts/import-stencil-png.mjs`
 * converts new artwork into this format at authoring time.
 *
 * All artwork here is original to this project.
 */

export type IconId =
  'heart' | 'star' | 'house' | 'tree' | 'moon' | 'cloud' | 'flower' | 'key' | 'boat' | 'mountain';

const ICON_ROWS: Record<IconId, readonly string[]> = {
  heart: [
    '................',
    '..####....####..',
    '.######..######.',
    '################',
    '################',
    '################',
    '.##############.',
    '.##############.',
    '..############..',
    '..############..',
    '...##########...',
    '....########....',
    '.....######.....',
    '......####......',
    '.......##.......',
    '................',
  ],
  star: [
    '.......##.......',
    '.......##.......',
    '......####......',
    '......####......',
    '.....######.....',
    '################',
    '.##############.',
    '..############..',
    '...##########...',
    '...##########...',
    '..############..',
    '..####....####..',
    '.####......####.',
    '.###........###.',
    '##............##',
    '................',
  ],
  house: [
    '.......##.......',
    '......####......',
    '.....######.....',
    '....########....',
    '...##########...',
    '..############..',
    '.##############.',
    '################',
    '..############..',
    '..############..',
    '..##..####..##..',
    '..##..####..##..',
    '..############..',
    '..#####..#####..',
    '..#####..#####..',
    '..#####..#####..',
  ],
  tree: [
    '.......##.......',
    '......####......',
    '.....######.....',
    '....########....',
    '...##########...',
    '..############..',
    '.....######.....',
    '....########....',
    '...##########...',
    '..############..',
    '.##############.',
    '.......##.......',
    '.......##.......',
    '.......##.......',
    '......####......',
    '.....######.....',
  ],
  moon: [
    '.....#####......',
    '...#######......',
    '..#####.........',
    '..####..........',
    '.####...........',
    '.####...........',
    '.###............',
    '.###............',
    '.###............',
    '.####...........',
    '.####...........',
    '..#####.........',
    '..#######.......',
    '...#########....',
    '.....#######....',
    '.......####.....',
  ],
  cloud: [
    '................',
    '.......####.....',
    '.....########...',
    '....##########..',
    '...############.',
    '..##############',
    '.###############',
    '################',
    '################',
    '.##############.',
    '..###########...',
    '................',
    '................',
    '................',
    '................',
    '................',
  ],
  flower: [
    '.....######.....',
    '....########....',
    '....########....',
    '.....######.....',
    '.####..##..####.',
    '######.##.######',
    '################',
    '################',
    '######.##.######',
    '.####..##..####.',
    '.....######.....',
    '....########....',
    '....########....',
    '.....######.....',
    '.......##.......',
    '.......##.......',
  ],
  key: [
    '....######......',
    '...########.....',
    '..###....###....',
    '..##......##....',
    '..##......##....',
    '..###....###....',
    '...########.....',
    '....######......',
    '.....####.......',
    '.....####.......',
    '.....####.......',
    '.....######.....',
    '.....####.......',
    '.....######.....',
    '.....####.......',
    '.....####.......',
  ],
  boat: [
    '.......##.......',
    '.......###......',
    '.......####.....',
    '.......#####....',
    '.......######...',
    '.......#######..',
    '.......########.',
    '.......#########',
    '.......##.......',
    '................',
    '################',
    '.##############.',
    '..############..',
    '...##########...',
    '.....######.....',
    '................',
  ],
  mountain: [
    '................',
    '................',
    '.......##.......',
    '......####......',
    '.....######.....',
    '....########....',
    '....########....',
    '...##########...',
    '...##########...',
    '..############..',
    '..############..',
    '.##############.',
    '.##############.',
    '################',
    '################',
    '................',
  ],
};

export const ICON_IDS = Object.keys(ICON_ROWS).sort() as IconId[];

export function drawIcon(id: IconId): Mask {
  const rows = ICON_ROWS[id];
  if (rows === undefined) throw new Error(`Unknown icon: ${id}`);
  return maskFromRows(rows);
}

/** Compact 5x7 glyph font used for numeral and letter silhouettes. */
const GLYPH_ROWS: Record<string, readonly string[]> = {
  '0': ['.###.', '#...#', '#..##', '#.#.#', '##..#', '#...#', '.###.'],
  '1': ['..#..', '.##..', '..#..', '..#..', '..#..', '..#..', '.###.'],
  '2': ['.###.', '#...#', '....#', '...#.', '..#..', '.#...', '#####'],
  '3': ['#####', '...#.', '..##.', '....#', '....#', '#...#', '.###.'],
  '4': ['...#.', '..##.', '.#.#.', '#..#.', '#####', '...#.', '...#.'],
  '5': ['#####', '#....', '####.', '....#', '....#', '#...#', '.###.'],
  '6': ['..##.', '.#...', '#....', '####.', '#...#', '#...#', '.###.'],
  '7': ['#####', '....#', '...#.', '..#..', '.#...', '.#...', '.#...'],
  '8': ['.###.', '#...#', '#...#', '.###.', '#...#', '#...#', '.###.'],
  '9': ['.###.', '#...#', '#...#', '.####', '....#', '...#.', '.##..'],
  A: ['..#..', '.#.#.', '#...#', '#...#', '#####', '#...#', '#...#'],
  B: ['####.', '#...#', '#...#', '####.', '#...#', '#...#', '####.'],
  C: ['.####', '#....', '#....', '#....', '#....', '#....', '.####'],
  E: ['#####', '#....', '#....', '####.', '#....', '#....', '#####'],
  H: ['#...#', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'],
  N: ['#...#', '##..#', '##..#', '#.#.#', '#..##', '#..##', '#...#'],
  O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
  T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  U: ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  X: ['#...#', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '#...#'],
  Z: ['#####', '....#', '...#.', '..#..', '.#...', '#....', '#####'],
};

export const GLYPH_IDS = Object.keys(GLYPH_ROWS).sort();

export function drawGlyph(character: string): Mask {
  const rows = GLYPH_ROWS[character];
  if (rows === undefined) throw new Error(`Unknown glyph: ${character}`);
  return maskFromRows(rows);
}
